import { features } from 'constants/featureFlags';
import useSpacesList from 'hooks/useSpacesList';
import { TFunction } from 'i18next';
import { Space } from 'interfaces/space';
import { useRouter } from 'next/router';
import { Controller, useForm } from 'react-hook-form';
import { PostBody } from 'types/post';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Select } from '@thinkific/chalk/Select';
import { Text } from '@thinkific/chalk/Text';

import { zodResolver } from '@hookform/resolvers/zod';

import { useCommunityContext } from 'components/CommunityContext';
import { getErrorMap } from 'components/Forms/utils/defaultErrorMap';
import RichTextEditor, { RichTextEditorHandle } from 'components/RichTextEditor/RichTextEditor';
import UserAvatar from 'components/UserAvatar';
import { useDraftPersistence } from 'components/new/Posts/hooks/useDraftPersistance';
import { useGlobalState } from 'components/new/providers/GlobalStateProvider';

import { getSchema, getSchemaOld } from 'schema/Post';
import { Attachment } from 'schema/Post/types';

import * as css from './PostForm.css';

interface PostFormProps {
  initialValues?: {
    title: string;
    content: string;
    body: PostBody | null;
    spaceId: string;
    attachments: Attachment[];
    mentionedUserIds: string[];
  };
  isEdit?: boolean;
  cancelEdit?: () => void;
  onSubmit: (
    data: FormValues,
    { reset, editorRef }: { reset: () => void; editorRef: React.RefObject<RichTextEditorHandle> }
  ) => Promise<void>;
  isSubmitting: boolean;
  spacesList: { value: string; label: string; dangerousFontFamily?: string }[];
  selectedSpace: Space;
  showSpaceSelect?: boolean;
  t: TFunction;
}

export interface FormValues {
  title: string;
  content: string;
  body: PostBody | null;
  spaceId: string;
  attachments: Attachment[];
  mentionedUserIds: string[];
}

const PostForm = ({
  initialValues,
  isEdit = false,
  cancelEdit,
  onSubmit,
  isSubmitting,
  spacesList,
  selectedSpace,
  showSpaceSelect = false,
  t,
}: PostFormProps) => {
  const { currentUser, featureFlags } = useCommunityContext();
  const tiptapEditorEnabled = featureFlags[features.TIPTAP_EDITOR];
  const editorRef = useRef<RichTextEditorHandle>(null);
  const mentionsTextAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const { getDraftPost, removeDraftPost } = useGlobalState();
  const { firstPostableSpace } = useSpacesList();
  const [selectedSpaceId, setSelectedSpaceId] = useState(
    selectedSpace?.id || firstPostableSpace?.value || ''
  );

  // Get user avatar info
  const { id: userId, avatarUrl, fullName } = currentUser;

  const newPost = (spaceId: string) => ({
    title: '',
    content: '',
    body: {
      jsonContent: null,
      plainText: '',
    },
    spaceId,
    attachments: [],
    mentionedUserIds: [],
  });

  const defaultValues =
    initialValues ||
    (selectedSpace && getDraftPost(selectedSpace.id)) ||
    newPost(selectedSpace?.id || firstPostableSpace?.value || '');

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues,
    resolver: zodResolver(tiptapEditorEnabled ? getSchema(t) : getSchemaOld(t), {
      errorMap: getErrorMap(t),
    }),
  });

  const router = useRouter();

  useEffect(() => {
    // If the spaceId is not set, set it to the first postable space
    const spaceId = getValues('spaceId');
    if (isSubmitting) return;
    if (!spaceId || (router.query.spaceId && selectedSpaceId !== router.query.spaceId)) {
      const fallbackSpaceId = selectedSpace?.id || firstPostableSpace?.value || '';
      setValue('spaceId', fallbackSpaceId);
      setSelectedSpaceId(fallbackSpaceId);
    }
  }, [spacesList, selectedSpace, router.query.spaceId, isSubmitting]);

  const { saveDraft } = useDraftPersistence(
    getValues,
    selectedSpaceId,
    isEdit,
    tiptapEditorEnabled
  );

  const resetDraft = useCallback(
    (spaceId: string) => {
      if (isEdit) return;
      const draft = getDraftPost(spaceId);
      reset(draft || newPost(spaceId));
    },
    [selectedSpace, defaultValues]
  );

  useEffect(() => {
    if (!tiptapEditorEnabled) return;
    if (selectedSpaceId && !isEdit) {
      resetDraft(selectedSpaceId);
    }
  }, [selectedSpaceId]);

  const handleFormSubmit = async (data) => {
    if (tiptapEditorEnabled) {
      removeDraftPost(data.spaceId);
    }
    await onSubmit(data, { reset, editorRef });
  };

  return (
    <div className={css.formWrapper} data-qa="post-form">
      <UserAvatar
        avatarUrl={avatarUrl}
        size="sm"
        userId={userId}
        username={fullName || ''}
        shouldOpenProfile
      />
      <div className={css.formLabel}>
        <div className={css.spaceSelectContainer}>
          <Text variant="body.md">
            {isEdit
              ? t('components-postForm-editing_post_in', 'Edit or move post to')
              : t('components-postForm-create_new_post_in', 'Create new post in')}
          </Text>
          {!showSpaceSelect ? (
            <Text variant="body.md">{selectedSpace?.name}</Text>
          ) : (
            <Controller
              control={control}
              name="spaceId"
              render={({ field }) => (
                <div data-user-pilot-id="post-form-space-select">
                  <Select
                    a11yLabel={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                    className={css.spaceSelect}
                    dataQa="post-form__space-select"
                    defaultValue={firstPostableSpace?.value}
                    label={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                    options={spacesList}
                    placeholder={t(
                      'components-forms-post-postForm-select_a_space',
                      'Select a space'
                    )}
                    value={field.value}
                    hiddenLabel
                    required
                    onChange={(value) => {
                      saveDraft(field.value);
                      setSelectedSpaceId(value);
                      field.onChange(value);
                      resetDraft(value);
                    }}
                  />
                </div>
              )}
            />
          )}
        </div>
      </div>

      <div
        className={css.editorWrapper}
        data-user-pilot-id={`${isEdit ? 'edit' : 'new'}-post-form`}
      >
        <RichTextEditor
          cancelEdit={cancelEdit}
          // @ts-ignore
          control={control}
          errors={errors}
          isEditing={isEdit}
          isSubmitting={isSubmitting}
          mentionsTextAreaRef={mentionsTextAreaRef}
          postType="post"
          ref={editorRef}
          setValue={setValue}
          spaceId={getValues('spaceId')}
          t={t}
          showTitle
          onSubmit={handleSubmit(handleFormSubmit)}
        />
      </div>
    </div>
  );
};

export default PostForm;
